import React, { useCallback, useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import _debounce from 'lodash/debounce';
import _differenceWith from 'lodash/differenceWith';
import _isNil from 'lodash/isNil';

import { IMerchant, useMerchantList, useMerchantsByIds } from 'api/merchants';

export const MerchantSelect = ({
  mode = null,
  placeholder = 'Please select a merchant',
  preSelected = false,
  value,
  ...props
}) => {
  const [searchString, setSearchString] = useState<string | null>(null);
  const [preselectedItem, setPreselectedItem] = useState<IMerchant[]>([]);
  const [items, setItems] = useState<IMerchant[]>([]);
  const [page, setPage] = useState<number>(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: selectedMerchants } = useMerchantsByIds(selectedIds);

  const { data, meta, isLoading, error } = useMerchantList({
    search_string: searchString,
    state: 'active',
    page
  });

  const onSearch = useCallback(val => {
    setItems([]);
    setSearchString(val);
  }, []);
  const onSelect = useCallback(() => setSearchString(null), []);

  useEffect(() => {
    if (data?.length) {
      setItems(oldItems => [...oldItems, ...data]);
    }
  }, [data]);

  useEffect(() => {
    const values: number[] = Array.isArray(value)
      ? value
      : _isNil(value) || value === ''
      ? []
      : [value];

    setSelectedIds(values);
  }, [value]);

  useEffect(() => {
    if (selectedMerchants) {
      setPreselectedItem(selectedMerchants);
    } else if (selectedIds.length === 0) {
      setPreselectedItem([]);
    }
  }, [selectedMerchants, selectedIds.length]);

  const handleScroll = e => {
    const { scrollTop, offsetHeight, scrollHeight } = e.target;
    const totalPage = meta?.total_pages || 0;
    if (!isLoading && scrollTop + offsetHeight >= scrollHeight - 500 && page < totalPage) {
      setPage(value => value + 1);
    }
  };

  if (error) {
    return null;
  }

  return (
    <Select
      showSearch
      value={value}
      data-testid="merchant-select"
      loading={isLoading}
      placeholder={isLoading ? 'Loading...' : placeholder}
      style={{ width: '100%' }}
      mode={mode}
      onSearch={_debounce(onSearch, 400)}
      onSelect={onSelect}
      onPopupScroll={handleScroll}
      {...props}
      onBlur={e => {
        setSearchString(null);
        e.preventDefault();
      }}
      filterOption={false}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
    >
      {preselectedItem.map(item => (
        <Select.Option key={item.id} value={item.id}>
          {item.name} (ID: {item.id})
        </Select.Option>
      ))}
      {_differenceWith(items, preselectedItem, (x, y) => x.id === y.id).map(option => (
        <Select.Option key={option.id} value={option.id}>
          {option.name} (ID: {option.id})
        </Select.Option>
      ))}
    </Select>
  );
};

export default MerchantSelect;
